// Admin "Drawing Files" card.
// Lets an admin attach two assets per design:
//   • floor_plan_pdf      (PDF) — the customer-facing 2D plan they can view
//   • technical_details_img (image) — a higher-detail elevation / section
//
// Each slot supports drag-and-drop OR a button-driven file picker, shows an
// inline preview state when populated (filename · upload date · View · Replace
// · Delete), and surfaces toasts via the parent's `onToast` callback so the
// look matches the rest of the admin app.
import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, FileText, Image as ImageIcon, ExternalLink, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 25;
const ACCEPT_PDF = ["application/pdf"];
const ACCEPT_IMG = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const ABS_URL = (rel) =>
  rel && rel.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${rel}` : rel;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Slot = ({ design, kind, label, accept, hint, current, uploadedAt, onUploaded, onToast }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const isPdf = kind === "pdf";
  const Icon = isPdf ? FileText : ImageIcon;

  const validate = (file) => {
    if (!file) return false;
    if (!accept.includes(file.type)) {
      onToast(`Invalid file type. Allowed: ${accept.join(", ")}`, "error");
      return false;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      onToast(`File too large (>${MAX_MB}MB)`, "error");
      return false;
    }
    return true;
  };

  const handleFiles = async (files) => {
    const file = files && files[0];
    if (!file || !validate(file)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/library/${design.id}/upload-drawing`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onToast("Upload successful", "success");
      onUploaded(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = e?.response?.status;
      const msg =
        code === 413
          ? `File too large (>${MAX_MB}MB)`
          : code === 415
          ? "Invalid file type"
          : detail || "Upload failed";
      onToast(msg, "error");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete the current ${label}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/library/${design.id}/drawing?kind=${kind}`);
      onToast(`${label} removed`, "success");
      onUploaded({ url: null, uploaded_at: null });
    } catch (e) {
      onToast(e?.response?.data?.detail || "Delete failed", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`drawing-slot-${kind}`}
      className="rounded-2xl border-2 border-dashed border-border bg-white"
    >
      <div className="p-3 border-b border-border">
        <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-extrabold">
          {label}
        </div>
        <div className="text-[11.5px] text-muted-foreground mt-0.5">{hint}</div>
      </div>

      {current ? (
        /* ── Populated preview state ── */
        <div className="p-3 flex items-center gap-3" data-testid={`drawing-slot-preview-${kind}`}>
          <div className="h-10 w-10 rounded-lg bg-gold/15 text-gold-dark grid place-items-center shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold text-navy truncate" title={current}>
              {current.split("/").pop()}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Uploaded {fmtDate(uploadedAt)}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <a
              href={ABS_URL(current)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`drawing-slot-view-${kind}`}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-navy text-gold text-[12px] font-extrabold hover:bg-navy-deep"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View
            </a>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid={`drawing-slot-replace-${kind}`}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-gold text-navy text-[12px] font-extrabold hover:bg-gold-dark hover:text-white disabled:opacity-50"
            >
              <Upload className="h-3.5 w-3.5" /> Replace
            </button>
            <button
              type="button"
              onClick={doDelete}
              disabled={busy}
              data-testid={`drawing-slot-delete-${kind}`}
              aria-label={`Delete ${label}`}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border-2 border-border bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        /* ── Empty drop zone ── */
        <div
          data-testid={`drawing-slot-dropzone-${kind}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => !busy && inputRef.current?.click()}
          className={`p-5 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
            drag ? "bg-gold/10" : "hover:bg-sand-alt/40"
          }`}
        >
          <div className="h-10 w-10 rounded-xl bg-navy/5 text-navy grid place-items-center mb-2">
            <Upload className="h-5 w-5" />
          </div>
          <div className="text-[13px] font-bold text-navy">
            {busy ? "Uploading…" : drag ? "Drop to upload" : "Drag & drop or click to choose"}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {isPdf ? "PDF only" : "JPG, PNG, WEBP"} · Max {MAX_MB}MB
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            data-testid={`drawing-slot-upload-${kind}`}
            className="mt-3 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-gold text-navy text-[12px] font-extrabold hover:bg-gold-dark hover:text-white disabled:opacity-50"
          >
            <Upload className="h-3.5 w-3.5" />
            {isPdf ? "Upload PDF" : "Upload Image"}
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept.join(",")}
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        data-testid={`drawing-slot-input-${kind}`}
      />
    </div>
  );
};

export const DrawingFilesCard = ({ design, onPatch, onToast }) => {
  // We optimistically merge the upload response into the parent's design
  // state so the preview updates instantly. The parent's `onPatch` callback
  // applies the same delta to its own copy of the design row.
  if (!design || !design.id) return null;
  return (
    <section className="sm:col-span-2 mt-2" data-testid="drawing-files-card">
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">
            Drawing Files
          </div>
          <div className="text-[12px] text-muted-foreground">
            Customer-facing floor plan + technical details. Saved to secure
            storage on upload.
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Slot
          design={design}
          kind="pdf"
          label="Floor Plan PDF"
          accept={ACCEPT_PDF}
          hint="2D floor plan delivered to the customer after purchase."
          current={design.floor_plan_pdf}
          uploadedAt={design.floor_plan_pdf_uploaded_at}
          onUploaded={(d) =>
            onPatch({
              floor_plan_pdf: d.url || null,
              floor_plan_pdf_uploaded_at: d.uploaded_at || null,
            })
          }
          onToast={onToast}
        />
        <Slot
          design={design}
          kind="image"
          label="Technical Details Image"
          accept={ACCEPT_IMG}
          hint="High-detail elevation or section preview (JPG/PNG/WEBP)."
          current={design.technical_details_img}
          uploadedAt={design.technical_details_img_uploaded_at}
          onUploaded={(d) =>
            onPatch({
              technical_details_img: d.url || null,
              technical_details_img_uploaded_at: d.uploaded_at || null,
            })
          }
          onToast={onToast}
        />
      </div>
    </section>
  );
};

export default DrawingFilesCard;
